import type { TFunction } from 'i18next';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import type { JSX } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { GoogleAdsCustomer } from '@feathr/blackbox';
import { EAdGrantStatus, EConnectionStatus } from '@feathr/blackbox';
import { Alert, Button, CardV2 as Card, Pill, toast, Well } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import useGoogleAdsQuotaToast from '@feathr/extender/hooks/useGoogleAdsQuotaToast';
import {
  isGoogleAdsQuotaExhaustedError,
  showGoogleAdsQuotaToast,
} from '@feathr/extender/hooks/useGoogleAdsQuotaToast.utils';
import { useStore } from '@feathr/extender/state';
import { connectionStatusInfo } from '@feathr/extender/styles/googleAdsConnectionStatus';

import GoogleAdsCallouts from './GoogleAdsCallouts';
import GoogleAdsCustomerConnect from './GoogleAdsCustomerConnect';

import * as styles from './GoogleAdsCustomersPage.css';

import googleAdsLogo from 'images/integrations/google_ads.png';

function showActionError(error: unknown, t: TFunction): void {
  if (isGoogleAdsQuotaExhaustedError(error)) {
    showGoogleAdsQuotaToast(t, 'account_connect');
    return;
  }

  toast(t("Couldn't complete action.\n{{- error}}", { error }), {
    type: 'error',
  });
}

function GoogleAdsCustomersPage(): JSX.Element {
  const { GoogleAdsCustomers } = useStore();
  const [customer, setCustomer] = useState<GoogleAdsCustomer | undefined>(undefined);
  const { t } = useTranslation();

  useGoogleAdsQuotaToast();

  const customers = GoogleAdsCustomers.list();

  const status = customer?.get('invite_status') ?? EConnectionStatus.disconnected;

  const dateIntegrated = moment
    .utc(customer?.get('date_integrated'), moment.ISO_8601)
    .format('MMMM Do YYYY');

  const { theme, text } = connectionStatusInfo(status, t, dateIntegrated);

  async function handleConnect(id: string): Promise<void> {
    try {
      const response = await GoogleAdsCustomers.linkCustomer({
        customerId: id,
        action: 'link',
      });

      const newCustomer = GoogleAdsCustomers.create(response);
      setCustomer(newCustomer);

      if (newCustomer.get('invite_status') === EConnectionStatus.pending) {
        toast(t('Waiting for invitation acceptance.'), {
          type: 'warning',
        });
      }
    } catch (error) {
      showActionError(error, t);
    }
  }

  async function handleDisconnect(): Promise<void> {
    try {
      if (customer) {
        const toastMessage =
          customer.get('invite_status') === EConnectionStatus.pending
            ? t('Reset Google Ad Grants connection.')
            : t('Disconnected Google Ad Grants.');

        const response = await GoogleAdsCustomers.linkCustomer({
          customerId: customer.get('customer_id'),
          action: 'unlink',
        });

        const { invite_status: connectionStatus } = response;

        customer.set({
          invite_status: connectionStatus,
        });

        toast(toastMessage, {
          type: 'success',
        });
      }
    } catch (error) {
      showActionError(error, t);
    }
  }

  const handleRefresh = useCallback(
    async (showToast = true): Promise<void> => {
      try {
        if (customer) {
          const { invite_status: connectionStatus, date_integrated: dateIntegrated } =
            await customer.refresh({
              customerId: customer.get('customer_id'),
            });

          customer.set({ invite_status: connectionStatus, date_integrated: dateIntegrated });

          if (showToast) {
            if (connectionStatus === EConnectionStatus.connected) {
              toast(t('Connected Google Ad Grants.'), {
                type: 'success',
              });
            } else {
              toast(t('Waiting for invitation acceptance.'), {
                type: 'warning',
              });
            }
          }
        }
      } catch (error) {
        showActionError(error, t);
      }
    },
    [customer, t],
  );

  useEffect(() => {
    const filteredCustomer = customers.models.find((customer) =>
      Object.values(EConnectionStatus).includes(customer.get('invite_status')),
    );

    if (filteredCustomer) {
      setCustomer(filteredCustomer);
      handleRefresh(false);
    }
  }, [customers.isPending, customers.models, handleRefresh]);

  const logo = (
    <div className={styles.logo}>
      <img alt={''} src={googleAdsLogo} />
    </div>
  );

  const showAdGrantStatusAlert =
    customer?.get('invite_status') === EConnectionStatus.disconnected &&
    customer?.get('ad_grant_status') === EAdGrantStatus.paid;

  return (
    <Page
      collapseHeader={false}
      collapseHeaderOnScroll={true}
      description={t(
        'Google Ads is an online advertising platform developed by Google, where advertisers bid to display brief advertisements, service offerings, product listings, or videos to web users. It can place ads both in the results of search engines like Google Search and on non-search websites, mobile apps, and videos.',
      )}
      loading={customers.isPending}
      title={t('Google Ads')}
    >
      <div className={styles.page}>
        {/* Wider than the CardV2 default (560px): the Callouts section below needs room
            for its 3-column row grid and ad preview without feeling cramped. */}
        <Card width={'wide'}>
          <Card.Header title={t('Google Ads Accounts')} titlePrefix={logo}>
            <GoogleAdsCustomerConnect
              key={'customer-connect'}
              onConnect={handleConnect}
              onDisconnect={handleDisconnect}
              onRefresh={handleRefresh}
              status={status}
            />
          </Card.Header>
          <Card.Content
            addVerticalGap={true}
            description={t(
              'Google Ad Grants shows your message to people searching for nonprofits like yours. Each qualifying nonprofit has access to up to $10,000 per month in search ads shown on Google.com. Additional Google Ads may be purchased in a separate account.',
            )}
          >
            {status === EConnectionStatus.connected && (
              <Well
                description={`ID: ${customer?.get('customer_id')}`}
                // descriptive_name is string or null on the model and label expects string or undefined
                label={customer?.get('descriptive_name') ?? undefined}
              />
            )}
            <Pill className={styles.chip} theme={theme}>
              {text}
            </Pill>
            {status === EConnectionStatus.pending && (
              <Alert
                className={styles.alert}
                description={t('The status will appear as pending until processed by Google.')}
                title={t(
                  'After accepting the email, it can take up to an hour for the connected status to be reflected here.',
                )}
                type={'info'}
              />
            )}
            {showAdGrantStatusAlert && (
              <Alert
                className={styles.alert}
                description={t(
                  "Account isn't a Google Ad Grants account. Try again with a Grants account, or contact support.",
                )}
                title={t('Unable to connect your account')}
                type={'warning'}
              />
            )}
            {status === EConnectionStatus.connected && customer && (
              <GoogleAdsCallouts customerId={customer.id} />
            )}
          </Card.Content>
          {status === EConnectionStatus.pending && (
            <Card.Content contentClassName={styles.footer} title={t('Having trouble connecting?')}>
              <Button onClick={handleDisconnect} type={'link'}>
                {t('Reset')}
              </Button>
            </Card.Content>
          )}
        </Card>
      </div>
    </Page>
  );
}

export default observer(GoogleAdsCustomersPage);
